import axios from 'axios';
import { useAuthStore } from '../features/auth/stores/useAuthStore';
import { ensureGuestSession } from '../providers/GuestProvider';
import i18n from '../i18n';
import { reportError, addBreadcrumb } from './catalyst';

// Create a central Axios instance for the storefront
export const api = axios.create({
    baseURL: (import.meta as any).env.VITE_API_BASE_URL || '/api',
    headers: {
        'Content-Type': 'application/json'
    }
});

// Request Interceptor: Attach Access Token (if logged in) or Guest ID
api.interceptors.request.use(async (config) => {
    let { accessToken, guestId, isAuthenticated } = useAuthStore.getState();
    addBreadcrumb(`${config.method?.toUpperCase()} ${config.url}`, 'api');

    // If accessing cart or wishlist while unauthenticated and guestId is missing,
    // ensure guest session is provisioned before the request departs!
    if (!isAuthenticated && !accessToken && !guestId) {
        const isCartOrWishlist = config.url && (config.url.includes('/cart') || config.url.includes('/wishlist'));
        if (isCartOrWishlist) {
            guestId = await ensureGuestSession();
        }
    }

    if (config.headers) {
        // Auth token has highest priority
        if (accessToken) {
            config.headers.Authorization = `Bearer ${accessToken}`;
        }

        // Always attach guest ID if available (backend will resolve priority)
        if (guestId) {
            config.headers['x-guest-id'] = guestId;
        }

        // Attach current language for backend localization
        if (i18n.language) {
            config.headers['Accept-Language'] = i18n.language;
        }
    }
    return config;
}, (error) => Promise.reject(error));

// Response Interceptor: Handle 401 & Auto-Refresh
api.interceptors.response.use(
    (response) => {
        // Automatically sync guestId if auto-provisioned or updated by the backend
        const serverGuestId = response.headers?.['x-guest-id'] || response.headers?.['X-Guest-Id'] || response.data?.guestId;
        if (serverGuestId && !useAuthStore.getState().isAuthenticated) {
            if (useAuthStore.getState().guestId !== serverGuestId) {
                useAuthStore.getState().setGuestId(serverGuestId);
            }
        }
        return response;
    },
    async (error) => {
        const originalRequest = error.config;

        // Ensure we don't get stuck in an infinite retry loop
        if (error.response?.status === 401 && !originalRequest._retry) {
            // Guard to prevent refreshing auth on public/guest endpoints if no user is actually logged in
            const state = useAuthStore.getState();
            if (!state.isAuthenticated || !state.refreshToken) {
                // If it's a 401 but we never logged in, just throw it (e.g. guest trying to review)
                return Promise.reject(error);
            }

            originalRequest._retry = true;

            try {
                // Attempt to refresh the token
                const refreshResponse = await axios.post('/api/auth/refresh', {
                    refreshToken: state.refreshToken
                });

                if (refreshResponse.data?.success) {
                    const { accessToken, refreshToken } = refreshResponse.data.data;

                    // Update Zustand store
                    state.setTokens(accessToken, refreshToken);

                    // Retry the failed request with the NEW token
                    originalRequest.headers.Authorization = `Bearer ${accessToken}`;
                    return api(originalRequest);
                }
            } catch (refreshError) {
                // If refresh fails (expired or invalid), log the user out seamlessly
                state.logout();
                return Promise.reject(refreshError);
            }
        }

        // Report 5xx errors or network failures to Catalyst
        // Exclude 4xx (client errors) like 404 Not Found, 401 Auth, etc.
        // Also exclude the catalyst ingest endpoint itself to avoid infinite loops
        if (!originalRequest.url?.includes('/catalyst/ingest')) {
            if (!error.response || (error.response.status >= 500)) {
                reportError(error, {
                    errorType: error.response ? 'ApiServerError' : 'NetworkError',
                    screenName: window.location.pathname,
                    severity: 'ERROR',
                    customData: {
                        url: originalRequest.url,
                        method: originalRequest.method,
                        status: error.response?.status,
                        data: error.response?.data
                    }
                });
            }
        }

        return Promise.reject(error);
    }
);
